import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigation } from "@/components/ui/navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import {
  ArrowLeft,
  ArrowUpRight,
  CheckCircle,
  XCircle,
  Target,
  TrendingUp,
  TrendingDown,
  Minus,
  Sparkles,
  BookOpen,
  GraduationCap,
  Play,
  ChevronRight,
  Calendar,
  Award,
  BarChart3,
  Info,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { fetchSubjectOutline } from "@/lib/api/subjects";
import { getSubjectMetadata, type SubjectColor } from "@/lib/subject-metadata";
import { hasUnitNotes } from "@/lib/notes-content";
import {
  fetchLessonPerformance,
  getSubjectPersonalization,
  saveSubjectPersonalization,
  calculatePredictedGrade,
  getRecommendedStudyOrder,
  calculateOverallStats,
  calculateProgressTrend,
  getProgressChartData,
  type LessonPerformance,
  type SubjectPersonalization,
} from "@/lib/api/personalization";

const GRADE_OPTIONS = [
  { value: "1", label: "1", description: "Needs significant improvement" },
  { value: "2", label: "2", description: "Below expectations" },
  { value: "3", label: "3", description: "Meets some expectations" },
  { value: "4", label: "4", description: "Meets expectations" },
  { value: "5", label: "5", description: "Exceeds expectations" },
];

const GRADE_COLORS: Record<string, string> = {
  "1": "text-red-600",
  "2": "text-orange-500",
  "3": "text-yellow-600",
  "4": "text-blue-600",
  "5": "text-emerald-600",
};

const COLOR_ICON_BG: Record<SubjectColor, string> = {
  history: "bg-subjects-history",
  science: "bg-subjects-science",
  math: "bg-subjects-math",
  social: "bg-subjects-social",
  economics: "bg-subjects-economics",
};

export default function PersonalizedLearning() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [targetGrade, setTargetGrade] = useState<string>("");
  const [targetInitialized, setTargetInitialized] = useState(false);

  // Fetch subject outline
  const outlineQuery = useQuery({
    queryKey: ["subject-outline", slug],
    queryFn: () => fetchSubjectOutline(slug!),
    enabled: Boolean(slug),
  });

  const subjectId = outlineQuery.data?.subject.id;
  const metadata = getSubjectMetadata(slug ?? "");

  // Fetch lesson performance from aggregated table
  const lessonPerformanceQuery = useQuery({
    queryKey: ["lesson-performance", user?.id, subjectId],
    queryFn: () => fetchLessonPerformance(user!.id, subjectId!),
    enabled: Boolean(user?.id && subjectId),
  });

  // Fetch saved personalization preferences
  const personalizationQuery = useQuery({
    queryKey: ["personalization", user?.id, subjectId],
    queryFn: () => getSubjectPersonalization(user!.id, subjectId!),
    enabled: Boolean(user?.id && subjectId),
  });

  // Save personalization mutation
  const savePersonalizationMutation = useMutation({
    mutationFn: (settings: SubjectPersonalization) =>
      saveSubjectPersonalization(user!.id, subjectId!, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["personalization", user?.id, subjectId] });
    },
  });

  // Calculate statistics from lesson performance
  const lessonStats = useMemo(
    () => lessonPerformanceQuery.data ?? [],
    [lessonPerformanceQuery.data]
  );
  const overallStats = useMemo(() => calculateOverallStats(lessonStats), [lessonStats]);
  const hasAttempts = overallStats.totalAttempts > 0;
  
  // Calculate predicted grade based on Readiness Score
  const predictedGrade = useMemo(
    () => hasAttempts ? calculatePredictedGrade(overallStats.averageMastery) : null,
    [hasAttempts, overallStats.averageMastery]
  );
  
  const recommendedLessons = useMemo(() => getRecommendedStudyOrder(lessonStats), [lessonStats]);
  const progressData = useMemo(() => getProgressChartData(lessonStats), [lessonStats]);

  // Separate strong and weak lessons based on mastery probability
  const strongLessons = useMemo(
    () => lessonStats.filter((lesson) => lesson.masteryProbability >= 0.7),
    [lessonStats]
  );
  const weakLessons = useMemo(
    () => lessonStats.filter((lesson) => lesson.masteryProbability < 0.7),
    [lessonStats]
  );

  // Initialize target grade from saved preferences
  useEffect(() => {
    if (targetInitialized) return;
    if (personalizationQuery.data) {
      setTargetGrade(personalizationQuery.data.targetGrade || "");
      setTargetInitialized(true);
    } else if (personalizationQuery.isSuccess && !personalizationQuery.data) {
      // If no data saved, just mark as initialized
      setTargetInitialized(true);
    }
  }, [personalizationQuery.data, personalizationQuery.isSuccess, targetInitialized]);

  // Save target grade when it changes
  const saveTargetGrade = useCallback(
    (target: string) => {
      if (!user?.id || !subjectId) return;
      savePersonalizationMutation.mutate({
        targetGrade: target,
        lastUpdated: new Date().toISOString(),
      });
    },
    [user?.id, subjectId, savePersonalizationMutation]
  );

  const handleTargetGradeChange = (value: string) => {
    setTargetGrade(value);
    saveTargetGrade(value);
  };

  // Calculate progress to target based on predicted grade
  const gradeGap = targetGrade && predictedGrade 
    ? parseInt(targetGrade) - parseInt(predictedGrade) 
    : 0;
  const progressToTarget = useMemo(() => {
    if (!targetGrade || !predictedGrade) return 0;
    const target = parseInt(targetGrade);
    const predicted = parseInt(predictedGrade);
    if (target <= 1) return 100; // Already at minimum, show full
    // Progress = how far predicted is toward target from 1
    return Math.min(100, Math.max(0, ((predicted - 1) / (target - 1)) * 100));
  }, [targetGrade, predictedGrade]);

  // Navigation to practice with specific lesson
  const handlePracticeLesson = (lesson: LessonPerformance) => {
    if (!outlineQuery.data) return;
    const params = new URLSearchParams();
    if (lesson.unitId) params.append("unitId", lesson.unitId);
    params.append("topicSlug", lesson.topicSlug);
    params.set("difficulty", "all");
    params.set("count", "10");
    navigate(`/practice/${slug}/test?${params.toString()}`);
  };

  // Start improving with recommended lessons (prioritize weak areas)
  const handleStartImproving = () => {
    if (!outlineQuery.data) return;
    const { units } = outlineQuery.data;
    const params = new URLSearchParams();

    const targetLessons = weakLessons.length > 0 ? weakLessons.slice(0, 5) : lessonStats.slice(0, 5);
    const unitIds = new Set<string>();

    targetLessons.forEach((lesson) => {
      if (lesson.unitId) unitIds.add(lesson.unitId);
      params.append("topicSlug", lesson.topicSlug);
    });

    if (targetLessons.length === 0) {
      units.forEach((unit) => {
        params.append("unitId", unit.id);
        unit.topics.forEach((topic) => {
          if (topic.slug) params.append("topicSlug", topic.slug);
        });
      });
    } else {
      unitIds.forEach((id) => params.append("unitId", id));
    }

    params.set("difficulty", "all");
    params.set("count", "20");
    navigate(`/practice/${slug}/test?${params.toString()}`);
  };

  // Get unit info for notes link - returns both ID (for link) and index (for hasUnitNotes check)
  const getUnitInfoForLesson = (lesson: LessonPerformance): { id: string; index: number } | null => {
    if (!lesson.unitId || !outlineQuery.data) return null;
    const unit = outlineQuery.data.units.find((u) => u.id === lesson.unitId);
    if (!unit) return null;
    return { id: unit.id, index: unit.index };
  };

  // Loading state
  if (outlineQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-xl">Loading personalized learning…</CardTitle>
              <CardDescription>Analyzing your performance data.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-32 rounded-lg bg-muted/40 animate-pulse" />
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Error state
  if (outlineQuery.isError || !outlineQuery.data) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-xl">Subject not found</CardTitle>
              <CardDescription>The subject you tried to open could not be located.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <Link to="/subjects">Browse Subjects</Link>
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const { subject } = outlineQuery.data;
  const subjectColor = metadata.color;

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <Badge variant="secondary" className="ml-1">
                Personalized Learning
              </Badge>
            </div>
            <Link
              to={`/subjects/${slug}`}
              className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
            >
              View all practice options <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          {/* Title Section */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-3 rounded-lg text-white ${COLOR_ICON_BG[subjectColor]}`}>
                <metadata.iconComponent className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">{subject.name ?? subject.slug}</h1>
                <p className="text-muted-foreground">Your personalized learning plan</p>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Grade Settings & Stats */}
            <div className="lg:col-span-1 space-y-6">
              {/* Predicted Grade Card - Only shows after target is set AND has practice data */}
              {targetGrade && predictedGrade && (
                <Card className="border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <GraduationCap className="h-5 w-5 text-primary" />
                      Your Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-help">
                            <div className="text-sm text-muted-foreground mb-1">
                              Predicted Grade
                              <Info className="h-3 w-3 inline-block ml-1 text-muted-foreground/60" />
                            </div>
                            <div className={`text-5xl font-bold ${GRADE_COLORS[predictedGrade]}`}>
                              {predictedGrade}
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="text-sm">
                            Your Readiness Score™ across {lessonStats.length} topics is{" "}
                            {Math.round(overallStats.averageMastery * 100)}%.
                            Getting questions right in a row builds your score faster. Early mistakes don&apos;t hurt you forever!
                          </p>
                        </TooltipContent>
                      </Tooltip>
                      <div className="text-sm text-muted-foreground mt-1">
                        {Math.round(overallStats.averageMastery * 100)}% Readiness across {lessonStats.length} topics
                      </div>
                    </div>

                    {gradeGap > 0 && (
                      <div className="space-y-2 pt-2 border-t">
                        <div className="flex justify-between text-sm">
                          <span>Progress to Grade {targetGrade}</span>
                          <span className="font-medium">
                            {Math.max(0, Math.min(100, Math.round(progressToTarget)))}%
                          </span>
                        </div>
                        <Progress value={Math.max(0, Math.min(100, progressToTarget))} className="h-2" />
                        <p className="text-xs text-muted-foreground text-center">
                          {gradeGap > 0
                            ? `${gradeGap} grade${gradeGap > 1 ? "s" : ""} to go!`
                            : "You've reached your target! 🎉"}
                        </p>
                      </div>
                    )}

                    {parseInt(predictedGrade) >= parseInt(targetGrade) && (
                      <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-center">
                        <Award className="h-6 w-6 text-emerald-600 mx-auto mb-1" />
                        <p className="text-sm text-emerald-700 font-medium">
                          You&apos;ve reached your target grade!
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* No Practice Yet Card - shows when target is set but no practice */}
              {targetGrade && !predictedGrade && (
                <Card className="border border-amber-200 bg-amber-50/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <GraduationCap className="h-5 w-5 text-amber-600" />
                      Start Practicing!
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-amber-800">
                      Complete some practice sessions to see your predicted grade. 
                      Our Readiness Score™ adapts to your learning patterns - recent improvements count more than early mistakes!
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Target Grade Card */}
              <Card className="border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="h-5 w-5 text-blue-500" />
                    Target Grade
                  </CardTitle>
                  <CardDescription>Set your goal to track progress</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">What grade are you aiming for?</label>
                    <Select value={targetGrade} onValueChange={handleTargetGradeChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your target grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {GRADE_OPTIONS.map((grade) => (
                          <SelectItem key={grade.value} value={grade.value}>
                            <span className="flex items-center gap-2">
                              <span className={`font-bold ${GRADE_COLORS[grade.value]}`}>{grade.label}</span>
                              <span className="text-muted-foreground">- {grade.description}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {!targetGrade && (
                    <p className="text-xs text-muted-foreground italic">
                      Set a target grade to see your progress tracker
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Overall Performance Card */}
              <Card className="border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-violet-500" />
                    Overall Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {overallStats.totalAttempts > 0 ? (
                    <div className="space-y-4">
                      <div className="text-center">
                        <div className="text-4xl font-bold">{Math.round(overallStats.averageMastery * 100)}%</div>
                        <p className="text-sm text-muted-foreground">
                          Average Readiness ({overallStats.totalCorrect} / {overallStats.totalAttempts} correct)
                        </p>
                      </div>
                      <Progress value={Math.round(overallStats.averageMastery * 100)} className="h-3" />
                      <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                        <div className="text-center">
                          <div className="text-2xl font-semibold text-emerald-600">{strongLessons.length}</div>
                          <div className="text-xs text-muted-foreground">Strong Lessons</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-semibold text-rose-600">{weakLessons.length}</div>
                          <div className="text-xs text-muted-foreground">Need Work</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Complete some practice tests to see your performance here.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Progress Over Time */}
              {progressData.length > 1 && (
                <Card className="border">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-amber-500" />
                      Progress Over Time
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {progressData.slice(-7).map((day) => (
                        <div key={day.date} className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-20">
                            {new Date(day.date).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all ${
                                day.percentage >= 70 ? "bg-emerald-500" : "bg-rose-400"
                              }`}
                              style={{ width: `${day.percentage}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium w-10 text-right">{day.percentage}%</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Start Improving Button */}
              <Button size="lg" className="w-full gap-2" onClick={handleStartImproving}>
                <Sparkles className="h-5 w-5" />
                {weakLessons.length > 0 ? "Start Improving Weak Areas" : "Continue Practicing"}
              </Button>
            </div>

            {/* Right Column - Lessons */}
            <div className="lg:col-span-2 space-y-6">
              {/* Recommended Study Order */}
              {recommendedLessons.length > 0 && weakLessons.length > 0 && (
                <Card className="border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                      <TrendingUp className="h-5 w-5" />
                      Recommended Study Order
                      <Badge className="ml-auto bg-amber-100 text-amber-800">
                        {Math.min(5, recommendedLessons.filter((l) => l.masteryProbability < 0.7).length)} lessons
                      </Badge>
                    </CardTitle>
                    <CardDescription className="text-amber-700">
                      Focus on these lessons first to improve your grade
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {recommendedLessons
                        .filter((l) => l.masteryProbability < 0.7)
                        .slice(0, 5)
                        .map((lesson, index) => (
                          <LessonCard
                            key={lesson.topicSlug}
                            lesson={lesson}
                            variant="recommended"
                            priority={index + 1}
                            slug={slug ?? ""}
                            unitInfo={getUnitInfoForLesson(lesson)}
                            onPractice={() => handlePracticeLesson(lesson)}
                          />
                        ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Strong Lessons */}
              <Card className="border border-emerald-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-emerald-700">
                    <CheckCircle className="h-5 w-5" />
                    Strong Lessons
                    {strongLessons.length > 0 && (
                      <Badge className="ml-auto bg-emerald-100 text-emerald-700">{strongLessons.length}</Badge>
                    )}
                  </CardTitle>
                  <CardDescription>Lessons where you have ≥70% Readiness</CardDescription>
                </CardHeader>
                <CardContent>
                  {strongLessons.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {strongLessons.map((lesson) => (
                        <LessonCard
                          key={lesson.topicSlug}
                          lesson={lesson}
                          variant="good"
                          slug={slug ?? ""}
                          unitInfo={getUnitInfoForLesson(lesson)}
                          onPractice={() => handlePracticeLesson(lesson)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="py-6 text-center">
                      <p className="text-sm text-muted-foreground">
                        {overallStats.totalAttempts > 0
                          ? "Keep practicing to build strong lessons!"
                          : "Complete practice tests to identify your strong lessons."}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Weak Lessons */}
              <Card className="border border-rose-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-rose-700">
                    <XCircle className="h-5 w-5" />
                    Lessons to Improve
                    {weakLessons.length > 0 && (
                      <Badge className="ml-auto bg-rose-100 text-rose-700">{weakLessons.length}</Badge>
                    )}
                  </CardTitle>
                  <CardDescription>Lessons that need more practice (&lt;70% Readiness)</CardDescription>
                </CardHeader>
                <CardContent>
                  {weakLessons.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {weakLessons.map((lesson) => (
                        <LessonCard
                          key={lesson.topicSlug}
                          lesson={lesson}
                          variant="bad"
                          slug={slug ?? ""}
                          unitInfo={getUnitInfoForLesson(lesson)}
                          onPractice={() => handlePracticeLesson(lesson)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="py-6 text-center">
                      <p className="text-sm text-muted-foreground">
                        {overallStats.totalAttempts > 0
                          ? "Great job! You're doing well in all lessons."
                          : "Complete practice tests to identify lessons for improvement."}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}

type LessonCardProps = {
  lesson: LessonPerformance;
  variant: "good" | "bad" | "recommended";
  slug: string;
  unitInfo: { id: string; index: number } | null;
  priority?: number;
  onPractice: () => void;
};

function LessonCard({ lesson, variant, slug, unitInfo, priority, onPractice }: LessonCardProps) {
  const trend = calculateProgressTrend(lesson.history);
  // Use unit index for hasUnitNotes check, but unit ID for the link
  const showNotes = unitInfo !== null && hasUnitNotes(slug, `unit-${unitInfo.index}`);
  const masteryPercent = Math.round(lesson.masteryProbability * 100);

  const bgClass =
    variant === "good"
      ? "bg-emerald-50 hover:bg-emerald-100"
      : variant === "recommended"
        ? "bg-white hover:bg-amber-50 border border-amber-200"
        : "bg-rose-50 hover:bg-rose-100";

  const progressClass =
    variant === "good" ? "bg-emerald-500" : variant === "recommended" ? "bg-amber-500" : "bg-rose-500";

  const textClass =
    variant === "good" ? "text-emerald-700" : variant === "recommended" ? "text-amber-700" : "text-rose-700";

  const TrendIcon = trend === "improving" ? TrendingUp : trend === "declining" ? TrendingDown : Minus;
  const trendColor =
    trend === "improving"
      ? "text-emerald-500"
      : trend === "declining"
        ? "text-rose-500"
        : "text-muted-foreground";

  return (
    <div className={`p-3 rounded-lg transition-colors ${bgClass}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {priority && (
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-200 text-amber-800 text-xs font-bold flex items-center justify-center">
              {priority}
            </span>
          )}
          <span className="text-sm font-medium truncate">{lesson.topicTitle ?? lesson.topicSlug}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {trend !== "unknown" && (
            <Tooltip>
              <TooltipTrigger>
                <TrendIcon className={`h-4 w-4 ${trendColor}`} />
              </TooltipTrigger>
              <TooltipContent>
                {trend === "improving"
                  ? "Readiness improving"
                  : trend === "declining"
                    ? "Readiness declining"
                    : "Readiness stable"}
              </TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger>
              <span className={`text-sm font-bold ${textClass}`}>{masteryPercent}%</span>
            </TooltipTrigger>
            <TooltipContent>
              Readiness Score™
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
        <div className={`h-full ${progressClass}`} style={{ width: `${masteryPercent}%` }} />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {lesson.correctCount} / {lesson.totalCount} correct
        </p>
        <div className="flex items-center gap-1">
          {showNotes && unitInfo && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 px-2" asChild>
                  <Link to={`/subjects/${slug}/notes/${unitInfo.id}?topic=${lesson.topicSlug}`}>
                    <BookOpen className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>View Notes</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={onPractice}>
                <Play className="h-3.5 w-3.5 mr-1" />
                <ChevronRight className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Practice this lesson</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
